import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  useCancelScheduledEmail,
  useScheduleEmail,
  useScheduledEmails,
  useSendEmailImmediately,
} from "api/hooks/useScheduledEmails";
import { ScheduledEmailApi, ScheduledEmailStatus } from "api/scheduledemails/scheduledemailsApi";
import { routes } from "consts";
import { PaziaButton } from "components/atoms";
import {
  PaziaModal,
  TanTable,
  TanTableColumn,
  useFilters,
  usePagination,
  useSorting,
} from "components/molecules";
import { formatDate } from "utils";
import { useToaster } from "utils";
import { EmailPageHeader } from "../components/EmailPageHeader";

const FILTER_NAME = "scheduled-emails-filter";

const STATUS_LABELS = {
  [ScheduledEmailStatus.Draft]: "Szkic",
  [ScheduledEmailStatus.Scheduled]: "Zaplanowane",
  [ScheduledEmailStatus.Sending]: "Wysyłanie",
  [ScheduledEmailStatus.Sent]: "Wysłane",
  [ScheduledEmailStatus.Failed]: "Niepowodzenie",
  [ScheduledEmailStatus.Cancelled]: "Anulowane",
};

const STATUS_COLORS = {
  [ScheduledEmailStatus.Draft]: "text-gray-600",
  [ScheduledEmailStatus.Scheduled]: "text-blue-600",
  [ScheduledEmailStatus.Sending]: "text-yellow-600",
  [ScheduledEmailStatus.Sent]: "text-green-600",
  [ScheduledEmailStatus.Failed]: "text-red-600",
  [ScheduledEmailStatus.Cancelled]: "text-gray-500",
};

export const ScheduledEmailsPage = () => {
  const { searchPhrase, filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("createdDate", "DESC");

  const navigate = useNavigate();
  const { showSuccess, showError } = useToaster();

  const { isFetching, isLoading, scheduledEmails, total, invalidate } = useScheduledEmails(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  const [emailToSchedule, setEmailToSchedule] = useState<ScheduledEmailApi | null>(null);
  const [scheduleDate, setScheduleDate] = useState<Date | null>(null);
  const [scheduleTime, setScheduleTime] = useState<string>("");

  const sendImmediatelyMutation = useSendEmailImmediately();
  const cancelMutation = useCancelScheduledEmail();
  const scheduleMutation = useScheduleEmail();

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const handleView = (email: ScheduledEmailApi) => {
    navigate(`${routes.EMAILS + routes.SCHEDULED_EMAILS}${email.id}`);
  };

  const handleScheduleConfirm = async () => {
    if (!emailToSchedule || !scheduleDate || !scheduleTime) {
      showError("Proszę wybrać datę i godzinę wysyłki");
      return;
    }

    try {
      const [hours, minutes] = scheduleTime.split(":");
      const deliveryDateTime = new Date(scheduleDate);
      deliveryDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      await scheduleMutation.mutateAsync({
        id: emailToSchedule.id,
        deliveryTime: deliveryDateTime.toISOString(),
      });

      showSuccess("Email został zaplanowany");
      setEmailToSchedule(null);
      setScheduleDate(null);
      setScheduleTime("");
      invalidate();
    } catch (error) {
      showError("Wystąpił błąd podczas planowania emaila");
    }
  };

  const handleScheduleCancel = () => {
    setEmailToSchedule(null);
    setScheduleDate(null);
    setScheduleTime("");
  };

  const cols: TanTableColumn<ScheduledEmailApi>[] = [
    {
      id: "title",
      accessorKey: "title",
      header: "Tytuł",
      size: 200,
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span>{row.original.title}</span>
          {row.original.isNewsletterSend && (
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
              Newsletter
            </span>
          )}
        </div>
      ),
    },
    {
      id: "subject",
      accessorKey: "subject",
      header: "Temat",
      size: 250,
      enableColumnFilter: false,
      enableSorting: true,
    },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      size: 120,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const status = info.getValue<ScheduledEmailStatus>();
        return <span className={`font-medium ${STATUS_COLORS[status]}`}>{STATUS_LABELS[status]}</span>;
      },
    },
    {
      id: "plannedDeliveryTime",
      accessorKey: "plannedDeliveryTime",
      header: "Planowana wysyłka",
      size: 150,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const date = info.getValue<Date>();
        const now = new Date();
        const isPast = date < now;
        const status = info.row.original.status;

        return (
          <span className={isPast && status === ScheduledEmailStatus.Scheduled ? "text-orange-500" : ""}>
            {formatDate(date, true, true)}
          </span>
        );
      },
    },
    {
      id: "recipients",
      accessorKey: "totalRecipients",
      header: "Odbiorcy",
      size: 80,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => {
        const total = info.getValue<number>();
        const sent = info.row.original.sentCount;
        const failed = info.row.original.failedCount;

        if (total === 0) return "0";

        return (
          <div className="text-sm">
            <div>{total} łącznie</div>
            {sent > 0 && <div className="text-green-600">{sent} wysłane</div>}
            {failed > 0 && <div className="text-red-600">{failed} błędy</div>}
          </div>
        );
      },
    },
    {
      id: "createdByUser",
      accessorKey: "createdByUser.email",
      header: "Utworzył",
      size: 150,
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Utworzono",
      size: 130,
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info) => formatDate(info.getValue<Date>(), true, true),
    },
  ];

  const columns = React.useMemo<TanTableColumn<ScheduledEmailApi>[]>(
    () => cols,
    [sendImmediatelyMutation.isPending, cancelMutation.isPending, scheduleMutation.isPending],
  );

  return (
    <div>
      <div className="flex justify-between">
        <EmailPageHeader>Wiadomości email</EmailPageHeader>
        <PaziaButton onClick={() => navigate(routes.EMAILS + routes.SCHEDULED_EMAILS_CREATE)} className="mb-4">
          Zaplanuj nowy email
        </PaziaButton>
      </div>

      <TanTable
        data={scheduledEmails}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        onRowClick={(email) => handleView(email)}
      />

      <PaziaModal isOpen={!!emailToSchedule} onCloseRequest={handleScheduleCancel} id="schedule-email-modal">
        <div className="p-6">
          <h3 className="mb-4 text-lg font-semibold">Zaplanuj wysyłkę emaila</h3>
          <div className="mb-4">
            <p className="mb-4 text-sm text-gray-600">
              Wybierz datę i godzinę wysyłki dla emaila: <strong>{emailToSchedule?.title}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Data wysyłki</label>
                <input
                  type="date"
                  value={scheduleDate ? scheduleDate.toISOString().split("T")[0] : ""}
                  onChange={(e) => setScheduleDate(e.target.value ? new Date(e.target.value) : null)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Godzina wysyłki</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <PaziaButton type="tertiary" onClick={handleScheduleCancel}>
              Anuluj
            </PaziaButton>
            <PaziaButton
              onClick={handleScheduleConfirm}
              disabled={scheduleMutation.isPending || !scheduleDate || !scheduleTime}
            >
              {scheduleMutation.isPending ? "Planowanie..." : "Zaplanuj"}
            </PaziaButton>
          </div>
        </div>
      </PaziaModal>
    </div>
  );
};
